/* eslint-disable */
import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useNavigate } from 'react-router-dom';

import urbanTheme from 'views/urban-dashboard/theme/urbanTheme';
import { useUrbanTransactions } from 'hooks/useUrbanTransactions';
import { formatIndianCurrency, formatIndianCount } from 'views/urban-dashboard/utils/formatters';
import { exportToExcel } from 'views/urban-dashboard/utils/exportToExcel';
import PerPageSelector from 'views/urban-dashboard/components/PerPageSelector';

const DATE_PRESETS = ['Today', 'Yesterday', 'This Week', 'This Month'];

/**
 * UrbanTransactionsList — Paginated transactions list with date presets and summary cards.
 */
function UrbanTransactionsList() {
    const navigate = useNavigate();
    const role = localStorage.getItem('role') === 'superdistributor' ? 'super-distributor' : 'distributor';
    const backPath = role === 'super-distributor' ? '/super-distributor/dashboard' : '/distributor/dashboard';

    const { state, data, summary, totalCount, pagination, error, dateType, setDateType, page, setPage, perPage, setPerPage, retry } = useUrbanTransactions({
        role
    });

    return (
        <ThemeProvider theme={urbanTheme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(160deg, #F8FAFC 0%, #EEF2FF 35%, #F0FDF4 70%, #F8FAFC 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-120px',
                        right: '-80px',
                        width: '400px',
                        height: '400px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%)',
                        pointerEvents: 'none'
                    }
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 3, sm: 4, md: 5 }, px: { xs: 2, sm: 3.5, md: 5 } }}>
                    <main style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <BackButton onClick={() => navigate(backPath)} />
                            <Box>
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{
                                        fontSize: { xs: '22px', sm: '28px' },
                                        fontWeight: 800,
                                        letterSpacing: '-0.03em',
                                        color: '#0F172A'
                                    }}
                                >
                                    Transactions List
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#64748B', mt: 0.25 }}>
                                    {totalCount > 0 ? `${formatIndianCount(totalCount)} transactions` : 'Transaction activity log'}
                                </Typography>
                            </Box>
                            {/* Export button */}
                            <ExportButton
                                onClick={() => {
                                    const endpoint =
                                        role === 'super-distributor'
                                            ? 'api/V2/moneytransfer/superDistributor/Urban/GetListTransactions'
                                            : 'api/V2/moneytransfer/distributor/Urban/GetListTransactions';
                                    const payload = { type: dateType };
                                    exportToExcel(
                                        endpoint,
                                        payload,
                                        'transactions_list',
                                        ['id', 'agent_name', 'shop_name', 'branch_name', 'amount', 'txn_status', 'created_at'],
                                        {
                                            id: 'ID',
                                            agent_name: 'Agent Name',
                                            shop_name: 'Shop Name',
                                            branch_name: 'Branch',
                                            amount: 'Amount',
                                            txn_status: 'Status',
                                            created_at: 'Date'
                                        }
                                    );
                                }}
                                disabled={state !== 'loaded'}
                            />
                        </Box>

                        {/* Date preset tabs */}
                        <Box sx={{ display: 'flex', gap: 0.75, mb: 3, flexWrap: 'wrap' }}>
                            {DATE_PRESETS.map((preset) => (
                                <FilterTab key={preset} label={preset} active={dateType === preset} onClick={() => setDateType(preset)} />
                            ))}
                        </Box>

                        {/* Summary cards */}
                        {state === 'loaded' && summary && <SummaryRow summary={summary} />}

                        {/* Content */}
                        {state === 'loading' && <LoadingSkeleton />}
                        {state === 'error' && <ErrorView message={error?.message} onRetry={retry} />}
                        {state === 'empty' && <EmptyView />}
                        {state === 'loaded' && (
                            <>
                                <TransactionTable data={data} />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 3 }}>
                                    <PerPageSelector value={perPage} onChange={setPerPage} />
                                    <Pagination
                                    page={page}
                                    lastPage={pagination.lastPage}
                                    currentPage={pagination.currentPage}
                                    setPage={setPage}
                                />
                                </Box>
                            </>
                        )}
                    </main>
                </Box>
            </Box>
        </ThemeProvider>
    );
}

/** Summary row with key stats */
function SummaryRow({ summary }) {
    const items = [
        { label: 'Total Volume', value: formatIndianCurrency(summary.totalVolume), color: '#0F172A' },
        { label: 'Success', value: formatIndianCount(summary.totalSuccessCounts), color: '#059669' },
        { label: 'Pending', value: formatIndianCount(summary.totalPendingCounts), color: '#D97706' },
        { label: 'Failed', value: formatIndianCount(summary.totalFailedCounts), color: '#DC2626' },
        { label: 'Agents', value: formatIndianCount(summary.totalDistinctAgents), color: '#4F46E5' }
    ];
    return (
        <Box sx={{ display: 'flex', gap: 1.25, mb: 3, flexWrap: 'wrap' }}>
            {items.map((item) => (
                <Box
                    key={item.label}
                    sx={{
                        flex: '1 1 120px',
                        px: 2,
                        py: 1.5,
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.85)',
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.02)'
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '10.5px',
                            fontWeight: 600,
                            color: '#94A3B8',
                            letterSpacing: '0.03em',
                            textTransform: 'uppercase',
                            mb: 0.5
                        }}
                    >
                        {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: item.color, fontFeatureSettings: '"tnum"' }}>
                        {item.value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

/** Transaction table */
function TransactionTable({ data }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {data.map((txn, i) => (
                <Box
                    key={txn.id || i}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr auto', sm: '2fr 1fr 1fr auto' },
                        gap: { xs: 0.5, sm: 2 },
                        px: { xs: 2, md: 2.5 },
                        py: { xs: 1.25, md: 1.5 },
                        borderRadius: '11px',
                        background: 'rgba(255, 255, 255, 0.65)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255, 255, 255, 0.8)',
                        alignItems: 'center',
                        transition: 'all 160ms ease',
                        '&:hover': { background: 'rgba(255,255,255,0.85)', transform: 'translateY(-0.5px)' }
                    }}
                >
                    {/* Agent + shop */}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#1E293B',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {txn.agent_name}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#94A3B8' }}>
                            {txn.shop_name} • {txn.branch_name}
                        </Typography>
                    </Box>

                    {/* Amount */}
                    <Typography
                        sx={{
                            fontSize: '13.5px',
                            fontWeight: 700,
                            color: '#0F172A',
                            fontFeatureSettings: '"tnum"',
                            display: { xs: 'none', sm: 'block' }
                        }}
                    >
                        {formatIndianCurrency(parseFloat(txn.amount) || 0)}
                    </Typography>

                    {/* Time */}
                    <Typography sx={{ fontSize: '11.5px', color: '#64748B', display: { xs: 'none', sm: 'block' } }}>
                        {txn.created_at
                            ? new Date(txn.created_at).toLocaleString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                              })
                            : '—'}
                    </Typography>

                    {/* Status */}
                    <TxnStatusBadge status={txn.txn_status} amount={txn.amount} />
                </Box>
            ))}
        </Box>
    );
}

function TxnStatusBadge({ status, amount }) {
    const isSuccess = status?.toLowerCase() === 'success';
    const isPending = status?.toLowerCase() === 'pending';
    const config = isSuccess
        ? { icon: <CheckCircleOutlineIcon />, color: '#059669', bg: 'rgba(16,185,129,0.06)', border: 'rgba(16,185,129,0.12)' }
        : isPending
        ? { icon: <HourglassEmptyIcon />, color: '#D97706', bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.12)' }
        : { icon: <ErrorOutlineIcon />, color: '#DC2626', bg: 'rgba(239,68,68,0.06)', border: 'rgba(239,68,68,0.12)' };

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.4,
                borderRadius: '7px',
                background: config.bg,
                border: `1px solid ${config.border}`
            }}
        >
            {React.cloneElement(config.icon, { sx: { fontSize: '13px', color: config.color } })}
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: config.color }}>{status}</Typography>
            {/* Show amount on mobile */}
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: config.color, display: { xs: 'inline', sm: 'none' }, ml: 0.5 }}>
                {formatIndianCurrency(parseFloat(amount) || 0)}
            </Typography>
        </Box>
    );
}

function FilterTab({ label, active, onClick }) {
    return (
        <Box
            component="button"
            onClick={onClick}
            sx={{
                px: 1.5,
                py: 0.6,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: active ? 'rgba(99,102,241,0.4)' : 'rgba(15,23,42,0.06)',
                background: active ? 'rgba(99,102,241,0.06)' : 'rgba(255,255,255,0.6)',
                backdropFilter: 'blur(6px)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'all 180ms ease',
                '&:hover': { borderColor: 'rgba(99,102,241,0.25)', background: 'rgba(255,255,255,0.85)' },
                '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
            }}
        >
            <Typography
                component="span"
                sx={{ fontSize: '12px', fontWeight: active ? 700 : 500, color: active ? '#4F46E5' : '#64748B', whiteSpace: 'nowrap' }}
            >
                {label}
            </Typography>
        </Box>
    );
}

function BackButton({ onClick }) {
    return (
        <Box
            component="button"
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: '1px solid rgba(15,23,42,0.08)',
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(8px)',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 200ms ease',
                '&:hover': { background: 'rgba(255,255,255,0.95)', borderColor: 'rgba(99,102,241,0.3)' },
                '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
            }}
        >
            <ArrowBackIcon sx={{ fontSize: '18px', color: '#475569' }} />
        </Box>
    );
}

function Pagination({ page, lastPage, currentPage, setPage }) {
    if (lastPage <= 1) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3 }}>
            <IconButton
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    border: '1px solid rgba(15,23,42,0.08)',
                    background: 'rgba(255,255,255,0.7)'
                }}
            >
                <ChevronLeftIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                {currentPage} / {lastPage}
            </Typography>
            <IconButton
                disabled={page >= lastPage}
                onClick={() => setPage(page + 1)}
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    border: '1px solid rgba(15,23,42,0.08)',
                    background: 'rgba(255,255,255,0.7)'
                }}
            >
                <ChevronRightIcon sx={{ fontSize: '18px' }} />
            </IconButton>
        </Box>
    );
}

function LoadingSkeleton() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {Array.from({ length: 8 }, (_, i) => (
                <Box
                    key={i}
                    sx={{
                        height: '50px',
                        borderRadius: '11px',
                        background: 'rgba(255,255,255,0.5)',
                        border: '1px solid rgba(255,255,255,0.7)',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${i * 0.06}s`,
                        '@keyframes pulse': { '0%,100%': { opacity: 0.5 }, '50%': { opacity: 1 } }
                    }}
                />
            ))}
        </Box>
    );
}

function ErrorView({ message, onRetry }) {
    return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontSize: '15px', color: '#64748B', mb: 2 }}>{message || 'Something went wrong.'}</Typography>
            <Box
                component="button"
                onClick={onRetry}
                sx={{
                    px: 3,
                    py: 1,
                    borderRadius: '10px',
                    border: '1px solid rgba(99,102,241,0.3)',
                    background: 'rgba(99,102,241,0.05)',
                    color: '#4F46E5',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    outline: 'none',
                    '&:hover': { background: 'rgba(99,102,241,0.1)' },
                    '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
                }}
            >
                Retry
            </Box>
        </Box>
    );
}

function EmptyView() {
    return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 2 }} />
            <Typography sx={{ fontSize: '15px', color: '#64748B' }}>No transactions found for this period.</Typography>
        </Box>
    );
}

function ExportButton({ onClick, disabled }) {
    return (
        <Box
            component="button"
            onClick={onClick}
            disabled={disabled}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.75,
                py: 0.85,
                ml: 'auto',
                borderRadius: '9px',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(8px)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'all 200ms ease',
                '&:hover:not(:disabled)': {
                    background: 'rgba(255,255,255,0.95)',
                    borderColor: 'rgba(99,102,241,0.3)',
                    boxShadow: '0 2px 8px -2px rgba(99,102,241,0.1)'
                },
                '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
            }}
        >
            <FileDownloadOutlinedIcon sx={{ fontSize: '16px', color: '#4F46E5' }} />
            <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap' }}>
                Export
            </Typography>
        </Box>
    );
}

export default UrbanTransactionsList;
